<script setup lang="ts">
import { computed, ref, watch, useTemplateRef } from "vue";
/** @ts-ignore */
import { Container, Draggable } from "vue3-smooth-dnd";
import ListingEditorPage from "./ListingEditorPage.vue";
import ListingEditorForm from "./ListingEditorForm.vue";
import FaqForm from "./FaqForm.vue";
import FaqCard from "./FaqCard.vue";
import PlanLimitNotice from "./PlanLimitNotice.vue";
import { useListingFaqsStore } from "./composables/useListingFaqsStore";
import { useListingFaq } from "./composables/useListingFaq";
import { useProfileDetails } from "./composables/useProfileDetails";
import { useDragRotation } from "@/app/composables/useDragRotation";
import type { Faq } from "./types/listingModels";

const props = defineProps<{
    locked?: boolean;
}>();

const asideOpen = ref(false);
const formEl = useTemplateRef('formEl');
const editIndex = ref<number | null>(null);
const formModel = ref<{ question: string; answer: string }>({ 
  question: '', 
  answer: '' 
});

// Use FAQs store directly
const faqsStore = useListingFaqsStore();
const { model: faqsModel, initial: faqsInitial } = faqsStore;

const { faqsLimit } = useProfileDetails();
const isAtLimit = computed(() => faqsLimit.value !== null && faqsModel.value.length >= faqsLimit.value);

// Load initial FAQs from API
const { data: faqsFromApi } = useListingFaq();
watch(faqsFromApi, (val) => {
  if (!val || !Array.isArray(val)) return;
  const mapped = val.map((f: any) => ({
    id: Number(f.id) || null,
    question: f.question || '',
    answer: f.answer || '',
  }));
  faqsInitial.value = mapped;
  faqsModel.value = mapped;
}, { immediate: true });

function onActionClick() {
  if (editIndex.value != null) {
    formEl.value?.resetForm();
  }
  editIndex.value = null;
  asideOpen.value = true;
}

function onSubmit(payload: Faq) {
  if (editIndex.value == null && isAtLimit.value) {
    asideOpen.value = false;
    return;
  }
  if (editIndex.value != null) {
    const current = faqsModel.value[editIndex.value];
    faqsStore.updateAt(editIndex.value, { 
      ...payload, 
      id: current?.id ?? null,
    });
  } else {
    // Generate a temporary unique ID for new items to ensure stable keys
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    faqsStore.add({ ...payload, id: null, _tempId: tempId } as Faq & { _tempId?: string });
  }
  formEl.value?.resetForm();
  asideOpen.value = false;
}

function onEdit(index: number) {
  editIndex.value = index;
  const item = faqsModel.value[index];
  if (!item) return;
  
  formModel.value = {
    question: item.question || '',
    answer: item.answer || '',
  };
  asideOpen.value = true;
}

function onDelete(index: number) {
  faqsStore.removeAt(index);
}

// Use drag rotation composable for dynamic rotation effects
const { onDragStart, onDragEnd } = useDragRotation({
  containerSelector: '.flex.flex-col',
  baseRotation: 0, // Rotation is calculated dynamically from velocity
  velocityMultiplierX: 3,
  velocityMultiplierY: 3.5,
  minAdjustment: -5.5,
  maxAdjustment: 5.5,
});

function onDrop(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
  if (dropResult.removedIndex !== null && dropResult.addedIndex !== null) {
    faqsStore.reorder(dropResult.removedIndex, dropResult.addedIndex);
  }
  onDragEnd();
}
</script>

<template>
  <ListingEditorPage 
    heading="FAQ" 
    action="Add FAQ" 
    :locked="props.locked"
    :action-disabled="isAtLimit"
    @action-click="onActionClick" 
    empty-state-text="No FAQs yet - but questions from clients deserve answers! Add common questions like 'What's your typical turnaround time?' or 'Do you offer ongoing support?'"
    empty-state-icon="file-placeholder"
  >
    <template #notice>
      <PlanLimitNotice feature="faqs" :limit="faqsLimit" :count="faqsModel.length" />
    </template>
    <template v-if="faqsModel.length" #content>
      <Container 
        @drop="onDrop"
        @drag-start="onDragStart"
        @drag-end="onDragEnd"
        drag-handle-selector=".drag-handle" 
        drag-class="dragging"
        :animation-duration="300"
        class="flex flex-col"
      >
        <Draggable v-for="(item, index) in faqsModel" :key="item.id || `faq-${item.question}-${item.answer}`">
        <FaqCard
          :item="item"
          :index="index"
            class="mb-4"
          @edit="onEdit(index)"
          @delete="onDelete(index)"
        />
        </Draggable>
      </Container>
    </template>
  </ListingEditorPage>

  <ListingEditorForm 
    :title="editIndex != null ? 'Edit FAQ' : 'Add FAQ'"
    description="Add a frequently asked question and provide a helpful answer for your clients."
    :submit-label="editIndex != null ? 'Save FAQ' : 'Add FAQ'"
    v-model:open="asideOpen"
  >
    <template #form>
      <FaqForm 
        ref="formEl"
        v-model="formModel"
        :submit-label="editIndex != null ? 'Save FAQ' : 'Add FAQ'"
        @submit="onSubmit"
      />
    </template>
  </ListingEditorForm>
</template>